/**
 * CombatStats - Health, mana, stamina, attack, defense data attached to any combatant.
 * Attach to a Character as character.combatStats = new CombatStats({...}).
 */

/** Primary attribute keys */
export const PRIMARY_ATTRS = ['str', 'dex', 'int', 'con', 'wis', 'end'];

/** Base attribute values at level 1 */
export const BASE_ATTRS = { str: 5, dex: 5, int: 5, con: 5, wis: 5, end: 5 };

/** NPC archetype attribute templates (weights sum to ~1.0) */
export const NPC_ATTR_TEMPLATES = {
  melee:  { str: 0.35, dex: 0.10, int: 0.00, con: 0.30, wis: 0.00, end: 0.25 },
  archer: { str: 0.10, dex: 0.35, int: 0.00, con: 0.20, wis: 0.00, end: 0.35 },
  mage:   { str: 0.00, dex: 0.05, int: 0.35, con: 0.15, wis: 0.35, end: 0.10 },
  tank:   { str: 0.20, dex: 0.05, int: 0.00, con: 0.40, wis: 0.00, end: 0.35 },
  boss:   { str: 0.25, dex: 0.10, int: 0.10, con: 0.30, wis: 0.05, end: 0.20 },
};

/** Spell-type projectiles (use mana, scale with spell damage) */
export const SPELL_PROJECTILES = ['fireball', 'icebolt', 'lightningbolt', 'poisonbolt', 'arcanebolt'];

export default class CombatStats {
  constructor(options = {}) {
    // Base stats
    this.maxHealth = options.maxHealth || 100;
    this.health = options.health ?? this.maxHealth;
    this.attackPower = options.attackPower || 10;
    this.defense = options.defense || 0;
    this.attackSpeed = options.attackSpeed || 1.0;

    // Mana
    this.maxMana = options.maxMana || 50;
    this.mana = options.mana ?? this.maxMana;

    // Stamina
    this.maxStamina = options.maxStamina || 100;
    this.stamina = options.stamina ?? this.maxStamina;

    // Regen rates (per second)
    this.healthRegen = options.healthRegen || 0.2;
    this.manaRegen = options.manaRegen || 0.5;
    this.staminaRegen = options.staminaRegen || 3.0;

    // Derived damage bonuses (computed from primary attributes)
    this.meleeDamageBonus = options.meleeDamageBonus || 0;
    this.rangedDamageBonus = options.rangedDamageBonus || 0;
    this.spellDamageBonus = options.spellDamageBonus || 0;

    // Level (for NPCs; player level lives in CharacterProgression)
    this.level = options.level || 1;

    // Equipment bonuses (set by LootManager)
    this.bonusAttack = 0;
    this.bonusDef = 0;
    this.bonusSpeed = 0;
    // Equipment attribute bonuses
    this.bonusStr = 0;
    this.bonusDex = 0;
    this.bonusInt = 0;
    this.bonusCon = 0;
    this.bonusWis = 0;
    this.bonusEnd = 0;

    // Health regen delay: blocks HP regen for X seconds after taking damage
    this.healthRegenDelay = 0;

    // Combat state
    this.isDead = false;
    this.isInvulnerable = false;
    this.invulnerabilityTimer = 0;
    this.invulnerabilityDuration = options.iFrames || 0.5;

    // Knockback
    this.knockbackVelocity = { x: 0, z: 0 };
    this.knockbackDecay = 15;

    // Loot table name (for enemies)
    this.lootTable = options.lootTable || 'default';
    this.isBoss = options.isBoss || false;
    this.isHostile = options.isHostile !== undefined ? options.isHostile : false;

    // NPC archetype (for type-aware damage)
    this.archetype = options.archetype || null;
  }

  /**
   * Build NPC attributes from an archetype template at a given level.
   * Uses the same formulas as player stats for consistency.
   * @param {number} level - NPC level
   * @param {string} archetype - Key into NPC_ATTR_TEMPLATES
   * @param {boolean} isBoss - Bosses get 1.5x attribute points
   * @returns {{attrs: Object, stats: Object}} attributes and derived combat stats
   */
  static buildNPCStats(level, archetype, isBoss = false) {
    const template = NPC_ATTR_TEMPLATES[archetype] || NPC_ATTR_TEMPLATES.melee;
    const totalPoints = (level - 1) * 3 * (isBoss ? 1.5 : 1);
    const attrs = { ...BASE_ATTRS };
    for (const attr of PRIMARY_ATTRS) {
      attrs[attr] += Math.floor(totalPoints * (template[attr] || 0));
    }
    return {
      attrs,
      ...CombatStats.deriveFromAttributes(attrs, level),
      archetype,
    };
  }

  /**
   * Derive combat stats from primary attributes (same formulas for player and NPC).
   * @param {Object} attrs - { str, dex, int, con, wis, end }
   * @param {number} [level=1] - For reference only (attackPower stays flat at 10)
   * @returns {Object} derived stats
   */
  static deriveFromAttributes(attrs) {
    return {
      maxHealth: 100 + attrs.con * 5,
      maxMana: 50 + attrs.int * 4 + attrs.wis * 2,
      maxStamina: 100 + attrs.end * 3,
      attackPower: 10,
      defense: 0,
      healthRegen: 0.2 + attrs.con * 0.04,
      manaRegen: 0.8 + attrs.wis * 0.35,
      staminaRegen: 4.0 + attrs.end * 0.4 + attrs.dex * 0.15,
      meleeDamageBonus: Math.floor(attrs.str * 0.5),
      rangedDamageBonus: Math.floor(attrs.dex * 0.55),
      spellDamageBonus: Math.floor(attrs.int * 0.8 + attrs.wis * 0.25),
      attackSpeed: 1.0 + attrs.dex * 0.015,
      maxCarryWeight: 100 + attrs.str * 2,
    };
  }

  /**
   * Legacy: Scale base stats for a given NPC level (kept for backward compat).
   */
  static scaleForLevel(baseStats, level) {
    return {
      maxHealth: Math.round(baseStats.maxHealth * (1 + (level - 1) * 0.10)),
      attackPower: Math.round(baseStats.attackPower * (1 + (level - 1) * 0.06)),
      defense: Math.round(baseStats.defense + (level - 1) * 0.12)
    };
  }

  // — Type-aware effective attack getters —

  getEffectiveAttack() {
    return this.attackPower + this.bonusAttack;
  }

  getEffectiveMeleeAttack() {
    return this.attackPower + this.bonusAttack + this.meleeDamageBonus;
  }

  getEffectiveRangedAttack() {
    return this.attackPower + this.bonusAttack + this.rangedDamageBonus;
  }

  getEffectiveSpellAttack() {
    return this.attackPower + this.bonusAttack + this.spellDamageBonus;
  }

  getEffectiveDefense() {
    return this.defense + this.bonusDef;
  }

  getEffectiveSpeed() {
    return this.attackSpeed + this.bonusSpeed;
  }

  /** Reset to full health/mana/stamina and clear combat state (e.g. on respawn). */
  reset() {
    this.health = this.maxHealth;
    this.mana = this.maxMana;
    this.stamina = this.maxStamina;
    this.isDead = false;
    this.isInvulnerable = false;
    this.invulnerabilityTimer = 0;
    this.healthRegenDelay = 0;
    this.knockbackVelocity.x = 0;
    this.knockbackVelocity.z = 0;
  }
}
